import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export default function LegalAdmin() {
  const qc = useQueryClient();
  const { data: pages = [] } = useQuery({ queryKey: ["admin-legal"], queryFn: async () => (await api.get("/admin/legal")).data });
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", title_ar: "", body_ar: "" });

  useEffect(() => {
    if (pages.length && !active) { setActive(pages[0]); setForm({ title: pages[0].title, body: pages[0].body, title_ar: pages[0].title_ar || "", body_ar: pages[0].body_ar || "" }); }
  }, [pages, active]);

  const select = (p) => { setActive(p); setForm({ title: p.title, body: p.body, title_ar: p.title_ar || "", body_ar: p.body_ar || "" }); };

  const save = useMutation({
    mutationFn: async () => api.put(`/admin/legal/${active.slug}`, form),
    onSuccess: () => { toast.success("Saved"); qc.invalidateQueries({ queryKey: ["admin-legal"] }); },
  });

  return (
    <div>
      <h1 className="font-heading text-2xl font-bold text-ink">Legal Content</h1>
      <p className="mt-1 text-sm text-subtle">Edit legal and regulatory documents.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[240px_1fr]">
        <div className="space-y-1">
          {pages.map((p) => (
            <button key={p.slug} onClick={() => select(p)} className={`block w-full rounded-lg px-3 py-2 text-left text-sm ${active?.slug === p.slug ? "bg-navy text-white" : "text-subtle hover:bg-canvas"}`} data-testid={`legal-tab-${p.slug}`}>
              {p.title}
            </button>
          ))}
        </div>
        {active && (
          <div className="rounded-xl border border-hair bg-white p-6 shadow-sm">
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Title</Label><Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="legal-title" /></div>
              <div className="space-y-1.5"><Label>Body (HTML)</Label><Textarea rows={14} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))} data-testid="legal-body" /></div>
              <div className="rounded-lg border border-dashed border-hair p-3 space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-finblue">العربية / Arabic</p>
                <div className="space-y-1.5"><Label>Title (AR)</Label><Input dir="rtl" value={form.title_ar} onChange={(e) => setForm((f) => ({ ...f, title_ar: e.target.value }))} data-testid="legal-title-ar" /></div>
                <div className="space-y-1.5"><Label>Body AR (HTML)</Label><Textarea dir="rtl" rows={10} value={form.body_ar} onChange={(e) => setForm((f) => ({ ...f, body_ar: e.target.value }))} data-testid="legal-body-ar" /></div>
              </div>
              <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-finblue hover:bg-finblueDark" data-testid="legal-save">{save.isPending ? "Saving..." : "Save Document"}</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
